import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Music } from "lucide-react";

/**
 * BackgroundAudio — plays the official Soul Trip theme song as a hidden
 * YouTube iframe via the YouTube IFrame API.
 *
 * Browsers require a user gesture to play audio with sound, so we:
 *  1. Mount the iframe muted on load (browsers allow muted autoplay)
 *  2. Show a floating "Tap to enable theme music" pulse for the first 6 seconds
 *  3. On click, unmute and let the music play. The same button toggles mute.
 */
export const BackgroundAudio = ({ ytId = "sMZZaf_bmiY" }) => {
  const playerRef = useRef(null);   // Holds the YouTube Player instance
  const playerDivRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [muted, setMuted] = useState(true);
  const [hideHint, setHideHint] = useState(false);

  // Load YouTube iframe API once
  useEffect(() => {
    const loadApi = () => {
      if (window.YT && window.YT.Player) {
        initPlayer();
        return;
      }
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
      window.onYouTubeIframeAPIReady = initPlayer;
    };

    const initPlayer = () => {
      if (!playerDivRef.current) return;
      playerRef.current = new window.YT.Player(playerDivRef.current, {
        videoId: ytId,
        height: "0",
        width: "0",
        playerVars: {
          autoplay: 1,
          loop: 1,
          playlist: ytId,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          fs: 0,
          iv_load_policy: 3,
        },
        events: {
          onReady: (e) => {
            try {
              e.target.mute();
              e.target.setVolume(60);
              e.target.playVideo();
              setReady(true);
            } catch (_) { /* ignore */ }
          },
        },
      });
    };

    loadApi();
    const hintTimer = setTimeout(() => setHideHint(true), 7000);

    return () => {
      clearTimeout(hintTimer);
      try { playerRef.current?.destroy?.(); } catch (_) { /* ignore */ }
    };
  }, [ytId]);

  const toggleMute = () => {
    const p = playerRef.current;
    if (!p) return;
    try {
      if (muted) {
        p.unMute();
        p.setVolume(60);
        p.playVideo();
        setMuted(false);
      } else {
        p.mute();
        setMuted(true);
      }
      setHideHint(true);
    } catch (_) { /* ignore */ }
  };

  return (
    <>
      {/* The hidden YouTube iframe — kept off-screen and tiny */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          top: -10,
          left: -10,
          width: 1,
          height: 1,
          overflow: "hidden",
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        <div ref={playerDivRef} />
      </div>

      {/* Floating control */}
      <button
        type="button"
        onClick={toggleMute}
        data-testid="bg-audio-toggle"
        aria-label={muted ? "Unmute background music" : "Mute background music"}
        className="fixed bottom-6 left-6 z-[700] st-interactive inline-flex items-center gap-2 px-3 py-2"
        style={{
          background: "rgba(5,4,3,.7)",
          border: `1px solid ${muted ? "rgba(201,160,74,.45)" : "#C9A04A"}`,
          backdropFilter: "blur(10px)",
          color: muted ? "var(--gold-lt)" : "var(--ink)",
          backgroundColor: muted ? "rgba(5,4,3,.7)" : "var(--gold)",
          transition: "background .25s, color .25s",
        }}
      >
        {muted ? <VolumeX size={14}/> : <Volume2 size={14}/>}
        <span className="st-cinzel text-[0.46rem] tracking-[0.35em] uppercase">
          {muted ? "Tap for Music" : "Theme · K-Music"}
        </span>
      </button>

      {/* First-time pulse hint near the button */}
      {ready && muted && !hideHint && (
        <div
          className="fixed bottom-[3.4rem] left-6 z-[699] pointer-events-none"
          style={{ animation: "bgHintBounce 1.6s ease-in-out infinite" }}
        >
          <div
            className="flex items-center gap-2 px-3 py-1.5 st-cinzel text-[0.45rem] tracking-[0.3em] uppercase text-[var(--ink)]"
            style={{ background: "var(--gold)", boxShadow: "0 8px 24px rgba(201,160,74,.4)" }}
          >
            <Music size={11}/> Tap for the theme ↓
          </div>
        </div>
      )}

      <style>{`
        @keyframes bgHintBounce {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50%      { transform: translateY(-6px); opacity: .85; }
        }
      `}</style>
    </>
  );
};
